/**
 * Talking back to the iMessage extension that is hosting this page.
 *
 * The extension shows the deck in a `WKWebView` rather than rebuilding it in
 * Swift, so this page is the only side of that pair that knows how the vote is
 * going — it is the one subscribed to the session. The bubble sitting in the
 * message thread wants to say so ("3 voting, Fiorella ahead"), and this is how
 * it finds out. See `MessagesViewController.swift` in mobile/targets/imessage.
 *
 * Every function here is a no-op in a normal browser, which is where this page
 * runs almost all of the time. Nothing checks whether the bridge exists before
 * calling: not being embedded is the common case, not an error.
 */

/**
 * The handler name the extension registers. Changing it means changing
 * `controller.add(self, name:)` on the Swift side in the same commit.
 */
const HANDLER = 'crave';

/**
 * Whether an embedded deck should create the session before joining it.
 *
 * Kept explicit rather than inferred from the invite roster: starting a brand
 * new group from Messages has nobody to invite yet, but the sender still needs
 * to become the first member.
 *
 * @param {string | null} raw
 * @returns {boolean}
 */
export function isHostLaunch(raw) {
  return raw === '1';
}

/**
 * Tells the host how the session is going.
 *
 * Two things at once, because they come from the same place and the extension
 * has no other source for either.
 *
 * `status` is what the bubble in the thread should say. `report` is who is in
 * the session and where the votes have landed, which the extension holds onto
 * and leaves in the shared container for the *app* to file as a meeting — an
 * evening spent swiping in a message thread has to end up in the same history
 * as one spent in the app, or the iMessage entry point is one that quietly does
 * not count. See `pendingMeetings.ts` on the other side.
 *
 * `report` is stringified here and stays a string all the way across. The
 * extension is transport for it and has no business knowing its shape; keeping
 * the schema in one language is worth one `JSON.parse` at the far end.
 *
 * Sent on every change rather than debounced: it is a `postMessage` inside one
 * process, and the extension decides when to actually act on it — it holds the
 * latest and writes once, on collapse.
 *
 * @param {object} state
 * @param {number} state.memberCount
 * @param {{id: string, name: string}[]} state.members Everyone but you.
 * @param {{id: number, name: string} | null} [state.pick] The place currently ahead.
 * @param {boolean} [state.ended] Whether voting has ended and the pick is final.
 */
export function postDeckState({ memberCount, members, pick, ended = false }) {
  const people = `${memberCount} ${memberCount === 1 ? 'person' : 'people'} voting`;
  const status = pick
    ? ended
      ? `Group picked ${pick.name}`
      : `${people} · ${pick.name} ahead`
    : ended
      ? 'Group voting ended'
      : people;

  try {
    window.webkit?.messageHandlers?.[HANDLER]?.postMessage({
      status,
      report: JSON.stringify({ members, pick: pick ?? null }),
    });
  } catch {
    // A host that went away mid-swipe. The deck itself is unaffected.
  }
}

/**
 * Reads the roster a host handed over in the URL.
 *
 * The extension cannot write to Firebase — there is no Firebase in Swift here —
 * so when a group is started from a message thread for people the app already
 * knows, the roster rides along as a query parameter and *this* page writes it
 * into the session as invites. Without that, a group restarted from iMessage
 * would arrive with no invited names, everyone would join as a stranger, and
 * the evening would be filed against a group that has never met.
 *
 * Treated as untrusted input, because it is: anybody can put anything in a URL.
 * The worst a bad one can do is seed a session with names nobody claims, but it
 * is still shaped-checked rather than parsed and trusted.
 *
 * @param {string | null} raw
 * @returns {{id: string, name: string}[]}
 */
export function readInvites(raw) {
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((item) => item && typeof item.id === 'string' && item.id.trim())
      .slice(0, 12)
      .map((item) => ({
        id: item.id.trim().slice(0, 64),
        name: (typeof item.name === 'string' ? item.name : '').trim().slice(0, 40) || 'Guest',
      }));
  } catch {
    return [];
  }
}
